// Save a version — the simplified commit, shared by Make changes (More actions
// › Save a version, scoped to the screen) and the Versions page (Advanced ›
// Source control). A version is a git commit of the files that hold the
// chosen screens / reports / menu, and ONLY those files (`scmCommit(…, {only})`),
// so a version of one screen never takes anybody else's work with it.
//
// `SaveVersionPanel` is the body (the Versions page shows it inline);
// `SaveVersionDialog` wraps it in a modal.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import Modal from '@/components/common/Modal'
import DiffViewer from '@/components/scm/DiffViewer'
import {
  getScmChanges, getScmCommit, getScmLog, scmCommit, scmPush,
} from '@/api/workbench'
import { getMyAccess } from '@/api/team'
import { refreshFlowDesigns } from '@/api/integration'
import { ProjectPermissions } from '@/auth/useProjectPermissions'
import type { ScmChangeGroup, ScmChanges, ScmCommit } from '@/types'

export interface SaveVersionObject {
  objType: string
  objName: string
  /** The name people know it by (its menu title). */
  title: string
}

export interface SaveVersionPanelProps {
  project: string
  /** Scope to one object ("Only <title>"). Absent = everything changed. */
  object?: SaveVersionObject
  /** Plain-words lines of what changed in this editing session (Make
   *  changes) — they write the suggested description. */
  changeLines?: string[]
  /** Bumped by the host to re-read the changes (after a save elsewhere). */
  refreshKey?: number
  onSaved?: (hash: string, shared: boolean) => void
  /** Show the earlier versions list (the dialog does; the Versions page has
   *  its own). */
  showVersions?: boolean
}

type Row = {
  key: string
  label: string
  kind: string
  files: string[]
  line: string
}

const keyOf = (g: ScmChangeGroup) => `${g.obj_type}:${g.obj_name.toLowerCase()}`

function errMsg(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  if (typeof d === 'string') return d
  return (e as Error)?.message || fallback
}

function fileCount(n: number) {
  return `${n} ${n === 1 ? 'file' : 'files'}`
}

/** Whether the caller may share (push) — `project.deploy`. */
function useCanShare(project: string): boolean {
  const [can, setCan] = useState(false)
  useEffect(() => {
    let cancelled = false
    getMyAccess(project)
      .then((a) => {
        if (!cancelled) {
          setCan(Boolean(a.is_owner || (a.permissions || []).includes(ProjectPermissions.DEPLOY)))
        }
      })
      .catch(() => { if (!cancelled) setCan(false) })
    return () => { cancelled = true }
  }, [project])
  return can
}

export function SaveVersionPanel({
  project, object, changeLines, refreshKey = 0, onSaved, showVersions = true,
}: SaveVersionPanelProps) {
  const [scope, setScope] = useState<'object' | 'all'>(object ? 'object' : 'all')
  const [changes, setChanges] = useState<ScmChanges | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [ticked, setTicked] = useState<Record<string, boolean>>({})
  const [includeMenu, setIncludeMenu] = useState(false)
  const [showFiles, setShowFiles] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [share, setShare] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState<string | null>(null)
  const [versions, setVersions] = useState<ScmCommit[]>([])
  const [compare, setCompare] = useState<{ sha: string; diff: string } | null>(null)
  const canShare = useCanShare(project)
  const scoped = scope === 'object' && !!object

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const res = await getScmChanges(project, scoped && object
        ? { objType: object.objType, objName: object.objName } : undefined)
      setChanges(res)
      // Everything is ticked except the shared menu.
      const next: Record<string, boolean> = {}
      for (const g of res.objects) next[keyOf(g)] = true
      setTicked(next)
      if (showVersions && res.is_repo) {
        const paths = scoped ? (res.patterns || []) : []
        const log = await getScmLog(project, 8, 0, paths)
        setVersions(log.commits)
      }
    } catch (e) {
      setLoadError(errMsg(e, 'Could not read what has changed'))
    }
  }, [project, scoped, object, showVersions])

  // The app's IOFlow flow designs are part of its source: write them now so
  // flows built or edited in IOFlow are offered here ("IOFlow flows").
  const [flowNonce, setFlowNonce] = useState(0)
  useEffect(() => {
    let cancelled = false
    refreshFlowDesigns(project).then((changed) => { if (changed && !cancelled) setFlowNonce((n) => n + 1) })
    return () => { cancelled = true }
  }, [project])

  useEffect(() => { void load() }, [load, refreshKey, flowNonce])

  const rows: Row[] = useMemo(() => {
    if (!changes) return []
    const out: Row[] = changes.objects.map((g) => {
      const mine = scoped && object && g.obj_name.toLowerCase() === object.objName.toLowerCase()
      const lines = mine && changeLines?.length ? changeLines.slice(-3) : []
      return {
        key: keyOf(g),
        label: g.title,
        kind: g.kind,
        files: g.files.map((f) => f.path),
        line: lines.length ? lines.join(' · ') : fileCount(g.files.length),
      }
    })
    if (!scoped && changes.other.length) {
      out.push({
        key: 'other', label: 'Other files', kind: 'Files',
        files: changes.other.map((f) => f.path), line: fileCount(changes.other.length),
      })
    }
    return out
  }, [changes, scoped, object, changeLines])

  // "Other files" is opt-in, like the menu.
  const isTicked = (r: Row) => (r.key === 'other' ? !!ticked.other : ticked[r.key] !== false)
  const chosen = rows.filter(isTicked)
  const menuFiles = changes?.menu?.files.map((f) => f.path) || []
  const paths = [
    ...chosen.flatMap((r) => r.files),
    ...(includeMenu ? menuFiles : []),
  ]
  const suggested = (() => {
    if (scoped && object) {
      const lines = (changeLines || []).slice(-4)
      return lines.length ? `${object.title}: ${lines.join('; ')}` : `${object.title}: changes`
    }
    return chosen.length ? `Changes to ${chosen.map((r) => r.label).join(', ')}` : ''
  })()
  const text = message ?? suggested
  const nothing = !!changes && rows.length === 0 && !menuFiles.length
  const upstream = !!changes?.upstream

  const save = async () => {
    if (!paths.length || !text.trim()) return
    setBusy(true)
    setError(null)
    try {
      const r = await scmCommit(project, text.trim(), paths, { only: true })
      const hash = String(r.commit_hash || '').slice(0, 7)
      let shared = false
      if (share && canShare) {
        try {
          const p = await scmPush(project)
          shared = !p.rejected
          if (p.rejected) setError(p.reason || 'Not shared: get the latest first, then share.')
        } catch (e) {
          setError(`Saved, but not shared: ${errMsg(e, 'the push failed')}`)
        }
      }
      setSavedNote(`Saved version ${hash}${shared ? ' and shared it' : ''}.`)
      setMessage(null)
      onSaved?.(hash, shared)
      await load()
    } catch (e) {
      setError(errMsg(e, 'Could not save the version'))
    } finally {
      setBusy(false)
    }
  }

  const openCompare = async (sha: string) => {
    if (compare?.sha === sha) { setCompare(null); return }
    try {
      const d = await getScmCommit(project, sha)
      setCompare({ sha, diff: d.diff })
    } catch (e) {
      setError(errMsg(e, 'Could not open that version'))
    }
  }

  if (loadError) return <p role="alert" style={{ color: 'var(--color-error)', margin: 0 }}>{loadError}</p>
  if (!changes) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--color-text-secondary)' }}>
        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Reading what changed…
      </span>
    )
  }
  if (!changes.is_repo) {
    return (
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        This project has no version history yet. Connect it to GitHub or GitLab under
        Advanced › Source control to start saving versions.
      </p>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-tw-screen="save-version">
      {object && (
        <div role="radiogroup" aria-label="What to save" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {([['object', `Only ${object.title}`], ['all', 'Everything changed in the app']] as const).map(([v, label]) => (
            <button key={v} type="button" role="radio" aria-checked={scope === v}
              onClick={() => { setScope(v); setMessage(null); setSavedNote(null) }}
              data-tw-action={`save-version-scope:${v}`}
              style={chip(scope === v)}>{label}</button>
          ))}
        </div>
      )}
      {savedNote && (
        <div role="status" style={{
          padding: '9px 12px', borderRadius: 8, fontSize: 14,
          background: 'color-mix(in srgb, #1a7f37 10%, transparent)', color: '#14532d',
          display: 'flex', alignItems: 'center', gap: 6,
        }}><Check size={14} /> {savedNote}</div>
      )}
      {nothing ? (
        <span style={{ color: 'var(--color-text-secondary)' }}>
          Nothing to save — every change is already in a version.
        </span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((r) => (
            <div key={r.key} style={{
              display: 'flex', flexDirection: 'column', gap: 4, padding: '9px 12px',
              border: '1px solid var(--color-border)', borderRadius: 8,
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: scoped ? 'default' : 'pointer' }}>
                {!scoped && (
                  <input type="checkbox" checked={isTicked(r)}
                    onChange={() => { setTicked((t) => ({ ...t, [r.key]: !isTicked(r) })); setMessage(null) }}
                    style={{ width: 18, height: 18, accentColor: 'var(--color-accent)' }} />
                )}
                <span style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <strong style={{ fontWeight: 600 }}>{r.label}</strong>
                  <span style={{ fontSize: 13.5, color: 'var(--color-text-secondary)' }}>{r.line}</span>
                </span>
                <span style={pill}>{r.kind}</span>
              </label>
              {showFiles && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: scoped ? 0 : 28 }}>
                  {r.files.map((f) => <code key={f} style={mono}>{f}</code>)}
                </div>
              )}
            </div>
          ))}
          {scoped && rows.length === 0 && (
            <span style={{ color: 'var(--color-text-secondary)' }}>
              {object?.title} has no changes that are not saved yet.
            </span>
          )}
        </div>
      )}
      {menuFiles.length > 0 && (
        <SwitchRow
          title="Include the menu"
          hint="The menu is shared by every screen — include it only if you moved or renamed something in it"
          on={includeMenu} onChange={setIncludeMenu} hook="save-version-menu" />
      )}
      {!nothing && (
        <button type="button" onClick={() => setShowFiles((v) => !v)} style={linkBtn}>
          {showFiles ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {showFiles ? 'Hide the files' : 'Show the files'}
        </button>
      )}
      <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>What changed (written for you — edit it if you like)</span>
        <input type="text" value={text} onChange={(e) => setMessage(e.target.value)}
          data-tw-field="save-version-message" style={input} />
      </label>
      {canShare && upstream && (
        <SwitchRow
          title="Also share it"
          hint="Send the version to the project’s GitHub / GitLab"
          on={share} onChange={setShare} hook="save-version-share" />
      )}
      {error && <p role="alert" style={{ margin: 0, color: 'var(--color-error)', fontSize: 14 }}>{error}</p>}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ flex: '1 1 220px', fontSize: 13, color: 'var(--color-text-secondary)' }}>
          {paths.length ? `Saves ${fileCount(paths.length)}. Everything else stays as it is.` : 'Tick what to save.'}
        </span>
        <button type="button" disabled={busy || !paths.length || !text.trim()} onClick={() => { void save() }}
          data-tw-action="save-version" style={primaryBtn(busy || !paths.length || !text.trim())}>
          {busy && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
          Save version
        </button>
      </div>
      {showVersions && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, borderTop: '1px solid var(--color-border)', paddingTop: 10 }}>
          <strong style={{ fontSize: 14 }}>Earlier versions</strong>
          {versions.length === 0 && <span style={{ color: 'var(--color-text-secondary)' }}>None yet.</span>}
          {versions.map((v) => (
            <div key={v.hash} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 240px', display: 'flex', flexDirection: 'column' }}>
                  <span>{v.subject}</span>
                  <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    {v.author} · {new Date(v.date).toLocaleString()}
                  </span>
                </span>
                <code style={mono}>{v.short_hash}</code>
                <button type="button" onClick={() => { void openCompare(v.hash) }} style={linkBtn}>
                  {compare?.sha === v.hash ? 'Close' : 'Compare'}
                </button>
              </div>
              {compare?.sha === v.hash && (
                <div style={{ maxHeight: 280, overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 6 }}>
                  <DiffViewer diff={compare.diff} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export interface SaveVersionDialogProps extends Omit<SaveVersionPanelProps, 'showVersions'> {
  open: boolean
  onClose: () => void
}

export default function SaveVersionDialog({ open, onClose, ...rest }: SaveVersionDialogProps) {
  const title = rest.object ? `Save a version of ${rest.object.title}` : 'Save a version of the app'
  return (
    <Modal open={open} onClose={onClose} title={title} size="wide">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
          A version is a saved copy you can go back to and compare with.
        </span>
        {open && <SaveVersionPanel {...rest} showVersions />}
      </div>
    </Modal>
  )
}

function SwitchRow({ title, hint, on, onChange, hook }: {
  title: string; hint: string; on: boolean; onChange: (v: boolean) => void; hook: string
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontWeight: 500 }}>{title}</span>
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{hint}</span>
      </span>
      <button type="button" role="switch" aria-checked={on} aria-label={title}
        onClick={() => onChange(!on)} data-tw-field={hook}
        style={{
          width: 40, height: 22, borderRadius: 11, border: 0, padding: 2, flexShrink: 0, cursor: 'pointer',
          display: 'flex', justifyContent: on ? 'flex-end' : 'flex-start',
          background: on ? 'var(--color-accent)' : 'var(--color-border)',
        }}>
        <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,.25)' }} />
      </button>
    </div>
  )
}

const chip = (on: boolean): React.CSSProperties => ({
  minHeight: 32, padding: '0 12px', borderRadius: 16, cursor: 'pointer', fontSize: 14,
  border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`,
  background: on ? 'color-mix(in srgb, var(--color-accent) 12%, transparent)' : 'transparent',
  color: 'var(--color-text)', fontWeight: on ? 600 : 400, fontFamily: 'inherit',
})
const pill: React.CSSProperties = {
  fontSize: 13, padding: '2px 9px', borderRadius: 999, fontWeight: 600,
  background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)',
}
const mono: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 13, color: 'var(--color-text-secondary)',
}
const linkBtn: React.CSSProperties = {
  alignSelf: 'flex-start', border: 0, background: 'transparent', padding: 0, cursor: 'pointer',
  color: 'var(--color-accent)', fontWeight: 600, fontSize: 14, fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', gap: 4,
}
const input: React.CSSProperties = {
  minHeight: 36, padding: '6px 10px', borderRadius: 6, boxSizing: 'border-box', width: '100%',
  border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
  fontSize: 14, fontFamily: 'inherit',
}
const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  minHeight: 38, padding: '0 16px', borderRadius: 8, border: 0, fontWeight: 600, fontSize: 14,
  fontFamily: 'inherit', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
  background: 'var(--color-accent)', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6,
})
